import React, { Component, Fragment } from 'react';
import { compose } from '../../reactApolloCompat';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import withStyles from '@mui/styles/withStyles';
import Typography from '../../component/Typography';
import Grid from '../../component/form/Grid';
import Paper from '@mui/material/Paper';
import { joinDefined, prettyDisposal } from '../../util/strings';
import { isNullOrUndefined } from '../../util/objects';
import { DotLegend } from '../../component/form/Legend';
import Table, { TableCell, TableRow } from '../../component/form/Table';
import { formatLocationDateTime, getMortuaryList } from './MortuaryListConstants';
import TickIcon from '../../component/icon/TickIcon';
import { getOfficeFromKey } from '../../util/brands';
import { InlineField } from '../../component/form/Inline';
import { theme } from '../../Theme.js';
import BrandedTableKey from '../../component/BrandedTableKey';
import { isRelatedObjectDefined } from '../../util/bookable';
import GatedComponent from '../../component/GatedComponent';
import moment from 'moment';
import { shortDateTimeFromString } from '../../util/date';
import cx from 'classnames';
import { PLACE_OF_SERVICE_TYPE_OPTIONS } from '../funeral/details/DetailsConstants';
import LinearProgress from '@mui/material/LinearProgress';
import BrandDot from '../../component/table/BrandDot';
import ErrorFallback from '../../component/ErrorFallback';
import { withErrorBoundary } from '@sentry/react';
import PrimaryButton from '../../component/form/PrimaryButton';

class MortuaryList extends Component {
    state = {
        mortuary: null,
        busy: true
    };

    componentDidMount() {
        getMortuaryList().then(mortuary => this.setState({ mortuary, busy: false }));
    }

    render() {
        const { mortuary } = this.state;
        const { classes } = this.props;
        return (
            <Fragment>
                <Typography variant="display4" gutterBottom className={classes.pageTitle}>
                    <span>Mortuary</span>
                    <small className={classes.header}>Action List</small>
                </Typography>
                {isNullOrUndefined(mortuary) ? this.renderLoading() : this.renderBody()}
            </Fragment>
        );
    }

    renderLoading() {
        const { classes } = this.props;

        return (
            <Paper className={classes.paper} elevation={0}>
                <LinearProgress />
            </Paper>
        );
    }

    renderKey() {
        return (
            <InlineField center wrap>
                <h5 className="key-heading">Legend:</h5>
                <BrandedTableKey />
                <DotLegend text="Not Ready for Checkout" dotColor={theme.palette.validation.error} />
                <DotLegend text="Details Changed" dotColor={theme.palette.validation.optional} />
            </InlineField>
        );
    }

    renderBody() {
        const { classes } = this.props;
        const { mortuary, busy } = this.state;

        if (mortuary.error) return <div>failed to get mortuary items: {mortuary.error}</div>;
        if (mortuary.items === null || mortuary.items.length === 0)
            return (
                <Paper className={classes.paper} elevation={0}>
                    <div>There are currently no action items.</div>
                </Paper>
            );

        return (
            <Paper className={classes.paper} elevation={0}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="title">
                            Action List - {mortuary.items.length} item{mortuary.items.length === 1 ? '' : 's'}
                            {mortuary.items.length < mortuary.totalCount && ` (of ${mortuary.totalCount})`}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3} style={{ opacity: busy ? 0.5 : 1, marginTop: -12 }}>
                    <GatedComponent
                        showComponentCode={'FM_ACCESS_Mortuary_View'}
                        isEnabledCode={'FM_ACCESS_Mortuary_Deceased_Action_View'}
                    >
                        {isEnabled => {
                            return (
                                <Grid item xs={12} className="sticky-table">
                                    <Table
                                        caption={this.renderKey()}
                                        className={cx(classes.table, 'table')}
                                        columns={[
                                            'Key',
                                            'Deceased',
                                            'Disposition',
                                            'Referee',
                                            'Accommodation',
                                            'Coffin Ready',
                                            'Viewing',
                                            'Service',
                                            'To be released to'
                                        ]}
                                    >
                                        {mortuary.items
                                            .sort((a, b) => this.sortNearestEvent(a, b))
                                            .map((mortuaryItem, index) =>
                                                this.renderRow(mortuaryItem, index, isEnabled)
                                            )}
                                    </Table>
                                    <div style={{ textAlign: 'center', paddingTop: 24 }}>
                                        {mortuary.hasNextPage ? (
                                            <PrimaryButton
                                                disabled={busy}
                                                onClick={() =>
                                                    this.setState({ busy: true }, () =>
                                                        getMortuaryList(mortuary.items.length).then(newData => {
                                                            newData.items = [].concat(mortuary.items, newData.items);
                                                            this.setState({ mortuary: newData, busy: false });
                                                        })
                                                    )
                                                }
                                            >
                                                {busy ? 'Loading...' : 'Load More Results'}
                                            </PrimaryButton>
                                        ) : (
                                            'End of results.'
                                        )}
                                    </div>
                                </Grid>
                            );
                        }}
                    </GatedComponent>
                </Grid>
            </Paper>
        );
    }

    firstViewing = item => {
        let latestDate = moment().add(100, 'years');
        let viewing = false;
        if (!!item.PlaceOfViewingRequired) {
            item.PlaceOfViewingItems.forEach(e => {
                const theDate = moment(e.Date + ' ' + e.Time);
                if (moment(theDate).isValid() && theDate < latestDate) {
                    latestDate = theDate;
                    viewing = e;
                }
            });
        }
        return viewing;
    };

    firstEvent = item => {
        const serviceObj = item.PlaceOfService.Type === 'Graveside' ? item.Disposal : item.PlaceOfService;
        const service = moment(serviceObj.Date + ' ' + serviceObj.Time);
        let latestDate = service.isValid() ? service : moment().add(100, 'years');
        let viewing = service.isValid() ? serviceObj : false;
        const theViewing = this.firstViewing(item);
        if (!!theViewing) {
            const theDate = moment(theViewing.Date + ' ' + theViewing.Time);
            if (moment(theDate).isValid() && theDate < latestDate) {
                viewing = theViewing;
            }
        }
        return viewing;
    };

    sortNearestEvent = (itemA, itemB) => {
        const eventA = this.firstEvent(itemA);
        const eventB = this.firstEvent(itemB);
        const timeA = (!!eventA && moment(eventA.Date + ' ' + eventA.Time)) || moment().add(100, 'years');
        const timeB = (!!eventB && moment(eventB.Date + ' ' + eventB.Time)) || moment().add(100, 'years');
        return timeA > timeB ? 1 : -1;
    };

    renderRow(mortuaryItem, index, canEdit) {
        const { classes } = this.props;

        const letterCode = getOfficeFromKey(mortuaryItem.LegacyKey);
        const rowStyle = 'row--' + letterCode.style;

        const isCremation = !!mortuaryItem.Disposal && mortuaryItem.Disposal.CrematedOrBuried === 'Cremated';
        const isGraveside =
            !!mortuaryItem.Disposal &&
            !!mortuaryItem.PlaceOfService &&
            mortuaryItem.PlaceOfService.Type === 'Graveside';
        const serviceObj = !!isGraveside ? mortuaryItem.Disposal : mortuaryItem.PlaceOfService;
        const serviceType = PLACE_OF_SERVICE_TYPE_OPTIONS.find(e => e.value === serviceObj.Type);

        const needsReferee =
            !!isCremation &&
            !(!!mortuaryItem.Certification && mortuaryItem.Certification.TypeOfBdmDeathCertificate === 'Coroner');

        const firstViewing = this.firstViewing(mortuaryItem);
        const releaseTo = serviceObj => {
            const service = moment(serviceObj.Date + ' ' + serviceObj.Time);
            let latestDate = service.isValid() ? service : moment('2100-01-01');
            let viewing = serviceObj;
            const theViewing = firstViewing;
            if (!!theViewing) {
                const theDate = moment(theViewing.Date + ' ' + theViewing.Time);
                if (moment(theDate).isValid() && theDate < latestDate) {
                    viewing = theViewing;
                }
            }
            return viewing;
        };

        return (
            <TableRow
                pad
                key={index}
                className={cx('row', rowStyle)}
                onClick={canEdit ? () => this.viewMortuaryItem(mortuaryItem, canEdit) : undefined}
            >
                <TableCell className={'td'} dataLabel={'Key'}>
                    {(!mortuaryItem.ReadyForMortuary || !!mortuaryItem.MortuaryWatchChanged) && (
                        <ul className={classes.dotList}>
                            {!mortuaryItem.ReadyForMortuary && (
                                <li>
                                    <DotLegend dotColor={theme.palette.validation.error} />
                                </li>
                            )}
                            {!!mortuaryItem.MortuaryWatchChanged && (
                                <li>
                                    <DotLegend dotColor={theme.palette.validation.optional} />
                                </li>
                            )}
                        </ul>
                    )}
                    <BrandDot dotColour={letterCode.color} label={mortuaryItem.LegacyKey} title={letterCode.label} />
                </TableCell>
                <TableCell className={'td'} dataLabel={'Deceased'}>
                    {joinDefined(
                        [(mortuaryItem.Surname || '').toUpperCase(), mortuaryItem.FirstName, mortuaryItem.MiddleName],
                        ' '
                    )}
                </TableCell>
                <TableCell className={'td td-center'} dataLabel={'Disposition'}>
                    {prettyDisposal(!!mortuaryItem.Disposal && mortuaryItem.Disposal.CrematedOrBuried)}
                </TableCell>
                <TableCell className={'td'} dataLabel={'Referee'}>
                    <span className={classes.iconContainer}>
                        {isCremation ? (
                            !!needsReferee ? (
                                !!isRelatedObjectDefined(mortuaryItem.Certification, 'Referee') ? (
                                    !!mortuaryItem.RefereeCremationCheck ? (
                                        <TickIcon className={classes.icon} title={'Completed'} />
                                    ) : (
                                        'In Progress'
                                    )
                                ) : (
                                    'NOT ASSIGNED'
                                )
                            ) : (
                                'Not Required'
                            )
                        ) : (
                            '-'
                        )}
                    </span>
                </TableCell>
                <TableCell className={'td'} dataLabel={'Accommodation'}>
                    {mortuaryItem.MortuaryLocation?.Name || '(inbound)'}
                </TableCell>
                <TableCell className={'td'} dataLabel={'Coffin Ready'}>
                    <span className={classes.iconContainer}>
                        {mortuaryItem.Disposal?.IsCoffinPrepared ? (
                            <TickIcon className={classes.icon} title={'Completed'} />
                        ) : mortuaryItem.Disposal.IsCoffinSelected ? (
                            'NOT READY'
                        ) : (
                            'None selected'
                        )}
                    </span>
                </TableCell>
                <TableCell className={'td'} dataLabel={'Viewing'}>
                    {firstViewing ? (
                        <div>
                            <strong>{firstViewing.Type}</strong>
                            <div>{shortDateTimeFromString(formatLocationDateTime(firstViewing))}</div>
                        </div>
                    ) : (
                        <span className={classes.iconContainer}>-</span>
                    )}
                </TableCell>
                <TableCell className={'td'} dataLabel={'Service'}>
                    <strong>{serviceType ? serviceType.label : 'TBC'}</strong>
                    <div>{shortDateTimeFromString(formatLocationDateTime(serviceObj))}</div>
                </TableCell>
                <TableCell className={'td'} dataLabel={'To be released to'}>
                    {isRelatedObjectDefined(serviceObj, 'Location') ? releaseTo(serviceObj).LocationFlattened : 'TBC'}
                </TableCell>
            </TableRow>
        );
    }

    viewMortuaryItem({ ID, LegacyKey }, canEdit) {
        const { history } = this.props;

        if (canEdit) {
            history.push({
                pathname: `/mortuary/item/${LegacyKey}/${ID}`
            });
        }
    }
}

const styles = ({ spacing, typography, palette, funeralHome }) => ({
    paper: {
        padding: spacing(3)
    },
    pageTitle: {
        color: palette.contentForeground[funeralHome]
    },
    header: {
        fontWeight: typography.fontWeightLight,
        marginLeft: 16,
        paddingLeft: 16,
        borderLeft: '1px solid ' + palette.action.active,
        color: palette.text.primary
    },
    wideScroll: {
        overflow: 'visible',
        overflowY: 'auto',
        padding: 12,
        paddingLeft: 24,
        marginLeft: -12
    },
    tableData: {
        marginTop: '1rem'
    },
    inline: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative'
    },
    dotList: {
        display: 'flex',
        margin: 0,
        padding: 0,
        listStyle: 'none'
    },
    dot: {
        height: 16,
        width: 16,
        borderRadius: 16
    },
    icon: {
        fontSize: 16,
        // color: '#26CC6F',
        height: '30px',
        width: '30px'
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center'
    },
    table: {
        '& .td-center': {
            textAlign: 'center'
        }
    },
    fireRow: {
        color: palette.validation.error + '!important'
    }
});

export default withErrorBoundary(compose(withSnackbarMessage, withStyles(styles))(MortuaryList), {
    fallback: ErrorFallback
});
